import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import IndentDetails from "../IndentPage/ItemsPage/IndentDetails";
import ItemsTable from "./L1ItemsTable";
import { useSelector } from "react-redux";
import IndentChangesTracker from "../IndentChangesTracker";
import { Loader2 } from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const L1InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { row: initialRow } = location.state || {};

  const [row, setRow] = useState(initialRow);
  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [error, setError] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [categories, setCategories] = useState([]);
  const currentUserId = useSelector((state) => state.auth.id);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });
  const [storedChangeLogs, setStoredChangeLogs] = useState([]);
  const [pageData, setPageData] = useState(null);

  const apiEndpoint = `${API_BASE_URL}/l1page/indents/${initialRow.IndentID}`;
  useEffect(() => {
    const fetchData = async () => {
      try {
        const indentResponse = await axios.get(apiEndpoint);
        // Handle both array and single object responses
        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data
          : [indentResponse.data];

        const indentsWithItems = await Promise.all(
          indentData.map(async (indent) => {
            if (!indent) return null;
            try {
              const itemsResponse = await axios.get(
                `${API_BASE_URL}/viewTable/indentitems/${initialRow.IndentID}`
              );

              return {
                ...indent,
                IndentItems: itemsResponse.data || [],
              };
            } catch (error) {
              console.error("Error fetching items:", error);
              return { ...indent, IndentItems: [] };
            }
          })
        );

        const validIndents = indentsWithItems.filter(Boolean);
        setPageData(validIndents);
      } catch (error) {
        console.error("Error fetching data:", error);
        setPageData([]);
      }
    };
    fetchData();
  }, []);

  // Error logging utility
  const logError = useCallback((context, error) => {
    console.error(`[L1InvoicingPage] ${context}:`, error);
    // Potential integration with error logging service
  }, []);

  // Fetch Indent Details with comprehensive error handling
  const fetchIndentDetails = useCallback(async () => {
    if (!initialRow?.IndentID) {
      setError("No Indent ID provided");
      return null;
    }

    try {
      // Note the change in URL structure
      const response = await axios.get(
        `${API_BASE_URL}/l1page/indents/${initialRow.IndentID}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data) {
        const updatedRow = {
          ...initialRow,
          ...response.data,
          UserID: response.data.UserID,
        };
        setRow(updatedRow);
        return updatedRow;
      }
    } catch (error) {
      logError("Fetch Indent Details", error);

      if (error.response?.status === 404) {
        setError(
          "Indent not found. It may have been deleted or does not exist."
        );
      } else if (error.response?.status === 401) {
        navigate("/login", {
          state: {
            message: "Your session has expired. Please log in again.",
          },
        });
      } else {
        setError("Failed to fetch indent details. Please try again.");
      }
    }
    return null;
  }, [initialRow?.IndentID, navigate, logError]);

  // Fetch stored change logs
  const fetchChangeLogs = useCallback(async () => {
    if (!row?.IndentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/change_log/change-logsl1/${row.IndentID}`,
        {
          params: { userId: currentUserId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setStoredChangeLogs(response.data || []);
    } catch (error) {
      logError("Fetch Change Logs", error);
      if (error.response?.status === 404) {
        setStoredChangeLogs([]);
      }
    }
  }, [row?.IndentID, currentUserId, logError]);

  // Save change log to backend
  const saveChangeLog = useCallback(
    async (changeLogData) => {
      try {
        await axios.post(
          `${API_BASE_URL}/change_log/change-logs`,
          {
            indentId: row.IndentID,
            userId: currentUserId,
            currentlevel: "L1",
            ...changeLogData,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } catch (error) {
        logError("Save Change Log", error);
        setError("Failed to save change log");
      }
    },
    [row?.IndentID, currentUserId, logError]
  );

  // Fetch draft items
  const fetchDraftItems = useCallback(async () => {
    if (row && row.IndentID) {
      try {
        const response = await axios.get(`${API_BASE_URL}/l1page/indentitems`, {
          params: { indentId: row.IndentID },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const indentStatus =
          response.data.length > 0
            ? {
                l1a: response.data[0].L1 || 0,
                l1: response.data[0].L1A || 0,
              }
            : { l1a: 0, l1: 0 };

        const draftItems = response.data.map((item) => ({
          indentItemID: item.IndentItemID,
          itemID: item.ItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          total: item.Quantity * (item.Stock || 0),
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
        }));

        setItems(draftItems);
        setIsDraft(true);

        // Update row status
        row.L1A = indentStatus.l1a;
        row.L1 = indentStatus.l1;
      } catch (error) {
        logError("Fetch Draft Items", error);

        if (error.response?.status === 404) {
          setError("No items found for this indent");
        } else if (error.response?.status === 401) {
          navigate("/login", {
            state: {
              message: "Your session has expired. Please log in again.",
            },
          });
        } else {
          setError("Failed to load draft items. Please try again.");
        }
      }
    }
  }, [row, navigate, logError]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoryResponse = await axios.get(
        `${API_BASE_URL}/dropdown/mcatdrop`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCategories(categoryResponse.data || []);
    } catch (error) {
      logError("Fetch Categories", error);
    }
  }, [logError]);

  // Effects
  useEffect(() => {
    fetchDraftItems();
    fetchChangeLogs();
    fetchCategories();
  }, [fetchDraftItems, fetchChangeLogs, fetchCategories]);

  // Item Update Handler
  const handleItemUpdate = useCallback(
    (updateInfo) => {
      setChangeLog((prev) => {
        const updatedLog = { ...prev };

        switch (updateInfo.type) {
          case "add":
            updatedLog.addedItems = [...prev.addedItems, updateInfo.item];
            saveChangeLog({
              type: "add",
              item: updateInfo.item,
              timestamp: new Date().toISOString(),
            });
            break;

          case "delete":
            // Ensure we capture all item details for deleted items
            const deletedItem = {
              ...updateInfo.item,
              deletedAt: new Date().toISOString(),
            };
            updatedLog.deletedItems = [...prev.deletedItems, deletedItem];
            saveChangeLog({
              type: "delete",
              item: deletedItem,
              timestamp: new Date().toISOString(),
            });
            break;

          case "update":
            const updateRecord = {
              original: items.find(
                (item) => item.itemID === updateInfo.item.itemID
              ),
              updated: updateInfo.item,
              timestamp: new Date().toISOString(),
            };
            updatedLog.updatedItems = [...prev.updatedItems, updateRecord];
            saveChangeLog({
              type: "update",
              original: updateRecord.original,
              updated: updateRecord.updated,
              timestamp: new Date().toISOString(),
            });
            break;

          default:
            break;
        }
        return updatedLog;
      });
    },
    [items, saveChangeLog]
  );

  // Add a cleanup effect to ensure change logs are saved before navigation
  useEffect(() => {
    return () => {
      if (changeLog.deletedItems.length > 0) {
        saveChangeLog({
          type: "bulk_delete",
          items: changeLog.deletedItems,
          timestamp: new Date().toISOString(),
        });
      }
    };
  }, [changeLog.deletedItems, saveChangeLog]);

  useEffect(() => {
    const initializePage = async () => {
      if (!initialRow) {
        setError("No indent selected. Please go back and select an indent.");
        return;
      }

      try {
        await fetchIndentDetails();
        await fetchDraftItems();
        await fetchChangeLogs();
        await fetchCategories();
      } catch (error) {
        logError("Page Initialization", error);
      }
    };

    initializePage();
  }, [
    initialRow,
    fetchIndentDetails,
    fetchDraftItems,
    fetchChangeLogs,
    fetchCategories,
    logError,
  ]);

  // Submission Handler
  const handleSubmit = useCallback(async () => {
    if (items.length === 0) {
      setError("Please add items before submitting");
      return;
    }

    try {
      const submissionData = {
        indentId: row.IndentID,
        userId: row.UserID,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          stock: item.stock,
          remarks: item.remarks || "",
          categoryID: item.categoryID,
          promisedDate: item.promisedDate,
          accountingUnitID: item.accountingUnitID || null,
        })),
      };

      const response = await axios.post(
        `${API_BASE_URL}/l1page/indents/submit`,
        submissionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Explicitly check for specific success conditions
      if (response.data && response.data.message) {
        setIsDraft(false);
        setIsEditable(false);

        navigate("/L1page", {
          state: {
            message: response.data.message || "Indent submitted successfully",
          },
        });
      }
    } catch (error) {
      // More detailed error handling
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Submission failed. Please try again.";

      setError(errorMessage);

      // Log the full error for debugging
      console.error("Submission Error:", error.response?.data || error);
    }
  }, [row, currentUserId, items, navigate]);

  // Save Draft Handler
  const handleSaveDraft = useCallback(async () => {
    if (!row || !row.IndentID) {
      setError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setError("Please add items before saving draft");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/l1page/indents/${row.IndentID}/draft`,
        {
          isDraft: true,
          items: items.map((item) => ({
            itemID: item.itemID,
            quantity: item.quantity,
            remarks: item.remarks,
            categoryID: item.categoryID,
            stock: item.stock,
            promisedDate: item.promisedDate,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      navigate("/L1page", {
        state: {
          message: "Draft saved successfully",
        },
      });
    } catch (error) {
      logError("Save Draft", error);
      setError("Failed to save draft. Please try again.");
    }
  }, [row, items, navigate, logError]);

  const handlePrint = () => {
    window.print();
  };

  if (!row) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow container mx-auto p-6 text-center">
          <p className="text-red-500">
            {error ||
              "No indent selected. Please go back and select an indent."}
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen relative">
     <Header
        exportEndpoint={apiEndpoint}
        importEndpoint={apiEndpoint}
        currentIndentData={pageData}
      />{" "}      
      {/* Full-page loading overlay */}
      {pageLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl flex items-center space-x-4">
            <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            <span className="text-lg font-medium text-gray-700">Processing...</span>
          </div>
        </div>
      )}

      <div className="flex-grow container mx-auto p-6">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">L1 Items</h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <IndentDetails row={row} />

        <ItemsTable
          indentID={row?.IndentID}
          items={items}
          onAddItem={(newItem) => setItems((prev) => [...prev, newItem])}
          onDeleteItem={(index) =>
            setItems((prev) => prev.filter((_, i) => i !== index))
          }
          onUpdateItem={handleItemUpdate}
          calculateTotalAmount={() =>
            items.reduce((acc, item) => acc + (item.total || 0), 0).toFixed(2)
          }
          isDraft={isDraft}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onPrint={handlePrint}
          isEditable={isEditable}
          indentStatus={{
            l1a: row?.L1A || 0,
            l1: row?.L1 || 0,
          }}
          setPageLoading={setPageLoading} // Pass the loading state setter
        />

        <IndentChangesTracker
          indentID={row?.IndentID.toString()}
          changes={changeLog}
          storedChangeLogs={storedChangeLogs}
        />
      </div>
      <Footer />
    </div>
  );
};


export default L1InvoicingPage;
